// Core cryptographic primitives — pure functions, no mutation

import { blake3 } from '@noble/hashes/blake3';
import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { ed25519 } from '@noble/curves/ed25519';
import { randomBytes as nobleRandomBytes } from '@noble/hashes/utils';
import { type Result, ok, err } from './types.js';
import { AEAD_NONCE_SIZE } from './constants.js';

const textEncoder = new TextEncoder();

/** BLAKE3 hash of arbitrary data. Always returns 32 bytes. */
export const hash = (data: Uint8Array): Uint8Array =>
  blake3(data);

/** Concatenate multiple Uint8Arrays into a single Uint8Array. */
export const concat = (...arrays: ReadonlyArray<Uint8Array>): Uint8Array => {
  const totalLength = arrays.reduce((acc, a) => acc + a.length, 0);
  const result = new Uint8Array(totalLength);
  arrays.reduce((offset, a) => {
    result.set(a, offset);
    return offset + a.length;
  }, 0);
  return result;
};

/**
 * Encrypt plaintext with XChaCha20-Poly1305.
 * Derives AEAD nonce deterministically: hash(key || "aead-nonce") sliced to 24 bytes.
 */
export const encrypt = (key: Uint8Array, plaintext: Uint8Array): Result<Uint8Array, string> => {
  try {
    const nonceInput = concat(key, textEncoder.encode('aead-nonce'));
    const nonce = hash(nonceInput).slice(0, AEAD_NONCE_SIZE);
    const cipher = xchacha20poly1305(key, nonce);
    return ok(cipher.encrypt(plaintext));
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};

/**
 * Decrypt ciphertext with XChaCha20-Poly1305.
 * Derives AEAD nonce deterministically: hash(key || "aead-nonce") sliced to 24 bytes.
 */
export const decrypt = (key: Uint8Array, ciphertext: Uint8Array): Result<Uint8Array, string> => {
  try {
    const nonceInput = concat(key, textEncoder.encode('aead-nonce'));
    const nonce = hash(nonceInput).slice(0, AEAD_NONCE_SIZE);
    const cipher = xchacha20poly1305(key, nonce);
    return ok(cipher.decrypt(ciphertext));
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};

/** Generate an Ed25519 keypair. */
export const generateKeypair = (): Readonly<{ publicKey: Uint8Array; privateKey: Uint8Array }> => {
  const privateKey = ed25519.utils.randomPrivateKey();
  const publicKey = ed25519.getPublicKey(privateKey);
  return Object.freeze({ publicKey, privateKey });
};

/** Sign a message with an Ed25519 private key. Returns a 64-byte signature. */
export const sign = (privateKey: Uint8Array, message: Uint8Array): Uint8Array =>
  ed25519.sign(message, privateKey);

/** Verify an Ed25519 signature. Returns true if valid. */
export const verify = (publicKey: Uint8Array, message: Uint8Array, signature: Uint8Array): boolean => {
  try {
    return ed25519.verify(signature, message, publicKey);
  } catch {
    return false;
  }
};

/** Derive the Ed25519 public key from a private key. */
export const getPublicKey = (privateKey: Uint8Array): Uint8Array =>
  ed25519.getPublicKey(privateKey);

/** Generate cryptographically secure random bytes. */
export const randomBytes = (n: number): Uint8Array =>
  nobleRandomBytes(n);
